import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useApp } from "~/core/store";
import { getCapacity, getRangeYears } from "./capacity";
import { CapacityBarProps } from "./interfaces";
import cn from "classnames";

const formatHours = (hours: number) => `${Math.round(hours * 10) / 10}h`;

// rendered only for rows visible in the virtual table, so data for an
// employee is requested once they scroll into view
const CapacityBar: FC<CapacityBarProps> = ({ employee, range }) => {
    const { api } = useApp();
    const assignments = api.task.assignment.assignee(employee._id);
    const assignmentsLoading = api.task.assignment.assigneeLoading(
        employee._id,
    );
    const years = getRangeYears(range).join(",");

    useEffect(() => {
        getRangeYears(range).forEach((year) =>
            api.employee.loadContracts(employee._id, year),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee.id, years]);

    const isLoading =
        assignmentsLoading.value || employee.loadingContracts.value;

    if (isLoading) {
        return (
            <div className="flex items-center gap-x-3">
                <div className="h-1.5 flex-1 animate-pulse rounded-full bg-gray-200" />
                <span className="w-24 text-right text-xs text-gray-300">
                    Calculating...
                </span>
            </div>
        );
    }

    const { available, occupied, free, percent } = getCapacity(
        assignments.value,
        employee.contracts.value,
        range,
    );
    const isBusy = percent >= 70 && percent < 100;
    const isFull = percent >= 100;

    return (
        <div
            title={`${formatHours(occupied)} planned of ${formatHours(available)} available`}
            className="flex items-center gap-x-3"
        >
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div
                    style={{ width: `${Math.min(percent, 100)}%` }}
                    className={cn(
                        "h-full rounded-full transition-[width] duration-500",
                        !isBusy && !isFull && "bg-lime-500",
                        isBusy && "bg-amber-400",
                        isFull && "bg-rose-500",
                    )}
                />
            </div>
            <span className="w-24 whitespace-nowrap text-right text-xs tabular-nums">
                <span
                    className={cn(
                        "font-bold",
                        !isBusy && !isFull && "text-lime-700",
                        isBusy && "text-amber-600",
                        isFull && "text-rose-600",
                    )}
                >
                    {Math.round(percent)}%
                </span>
                <span className="text-gray-400">
                    {" "}
                    · {formatHours(free)} free
                </span>
            </span>
        </div>
    );
};

export default CapacityBar;
